/**
 * Der zuletzt hinterlegte Ausweis eines Beteiligten — für den Vergleich beim
 * Prüfen einer Ident-Service-Erfassung (Ticket #1298).
 *
 * Seit `8eb3790c` liegen Mandanten-Dokumente als `Dokumenttyp → Version →
 * Dateien` vor. Beim Prüfen war bisher nur „Payload gegen Stammdaten" sichtbar
 * (`geändert`/`ergänzt` gegen `PrefillPayload`). Die fachlich interessante Frage
 * ist aber die andere: ist das der bekannte Ausweis, nur verlängert — oder ein
 * anderes Dokument?
 *
 * Ohne React-Abhängigkeit, damit die Ableitung ohne Rendering testbar bleibt.
 */

import {
  ILegalClient,
  ILegalClientFullObject,
} from '../Interfaces/ILegalClient';
import {
  ILegalClientDocumentFile,
  ILegalClientDocumentTypeEntry,
  ILegalClientDocumentVersion,
  getCurrentVersion,
} from '../Interfaces/ILegalClientDocumentVersion';

/** `LegalClientDocumentType` „Personalausweis" — die Ablage des Ident-Service. */
export const ID_DOC_TYPE_PERSONALAUSWEIS = 50;

/**
 * Dokumentart der Erfassung → `LegalClientDocumentType` (Ticket #1299).
 *
 * Der Vergleich muss den **gleichen** Typ gegenüberstellen: ein Personalausweis
 * gegen einen Reisepass ist keine Verlängerung, und der Ablauf-Hinweis „läuft
 * früher ab" wäre dort ein Fehlalarm.
 */
export type IdDocumentKind = 'id_card' | 'passport' | 'eu_id' | 'other';

export const ID_DOC_TYPE_BY_KIND: Record<IdDocumentKind, number> = {
  id_card: ID_DOC_TYPE_PERSONALAUSWEIS,
  passport: 51,
  eu_id: 52,
  other: 53,
};

export const ID_DOC_TYPE_LABEL: Record<number, string> = {
  50: 'Personalausweis',
  51: 'Reisepass',
  52: 'Anderer europäischer Personalausweis/Reisepass',
  53: 'Sonstiges Ausweisdokument',
};

/**
 * Alle Ausweis-Dokumentarten, in der Reihenfolge, in der sie zu bevorzugen
 * sind: der Personalausweis zuerst, dann der Reisepass, dann die Sonderfälle.
 */
export const ID_DOC_TYPE_ARRAY: number[] = [50, 51, 52, 53];

/**
 * Dokumenttyp der Ablage aus dem Payload.
 *
 * Erfassungen von vor #1299 tragen keine Angabe — dann bleibt es bei
 * „Personalausweis", genau wie die Ablage es damals gemacht hat.
 */
export function documentTypeFromPayload(
  documentType: string | null | undefined
): number {
  const kind = (documentType ?? '').toLowerCase() as IdDocumentKind;
  return ID_DOC_TYPE_BY_KIND[kind] ?? ID_DOC_TYPE_PERSONALAUSWEIS;
}

export interface StoredIdDocument {
  version: ILegalClientDocumentVersion;
  files: ILegalClientDocumentFile[];
  /** Anzahl aller Stände dieses Typs, für „Version 2 von 3". */
  versionCount: number;
}

/**
 * Nur der Teil des Beteiligten, den der Vergleich braucht.
 *
 * Die Beteiligten im Vorgang tragen ihre Dokument-Versionen zur Laufzeit mit:
 * `/legaltransaction/fullObject/{id}` liefert sie pro Beteiligtem, und
 * `customFetcher` validiert die Antwort nicht gegen ein zod-Schema — es wird
 * also nichts abgeschnitten (nachgesehen, nicht angenommen). Deklariert ist das
 * Feld aber nur an `ILegalClientFullObject`, während Panel und Dialog mit dem
 * schlanken `ILegalClient` typisiert sind.
 *
 * Statt den Prop-Typ durch den halben Vorgang zu ziehen — und damit auch die
 * Stellen zu berühren, die wirklich mit einem schlanken Mandanten arbeiten —
 * verlangt diese Ableitung genau das eine Feld.
 */
export type ParticipantWithDocuments = Partial<
  Pick<ILegalClientFullObject, 'DocumentTypeArray'>
>;

export type ComparableParticipant = ILegalClient | ParticipantWithDocuments;

function idTypeEntry(
  participant: ComparableParticipant | undefined | null,
  idDocumentType: number
): ILegalClientDocumentTypeEntry | undefined {
  const typeArray =
    (participant as ParticipantWithDocuments | undefined | null)
      ?.DocumentTypeArray ?? [];
  return typeArray.find(
    entry => entry.idLegalClientDocumentType === idDocumentType
  );
}

/**
 * Der aktuelle hinterlegte Stand des Ausweises, oder `null`, wenn zu diesem
 * Beteiligten noch keiner vorliegt.
 *
 * Typen ohne Version erscheinen im `DocumentTypeArray` ebenfalls (damit die
 * Oberfläche zeigen kann, was fehlt) — ein Eintrag allein bedeutet also nicht,
 * dass etwas hinterlegt ist.
 */
export function storedIdDocument(
  participant: ComparableParticipant | undefined | null,
  idDocumentType: number = ID_DOC_TYPE_PERSONALAUSWEIS
): StoredIdDocument | null {
  const entry = idTypeEntry(participant, idDocumentType);
  if (!entry || entry.VersionArray.length === 0) return null;
  const version = getCurrentVersion(entry);
  if (!version) return null;
  const files = [...(version.FileArray ?? [])].sort(
    (a, b) => (a.Position ?? 0) - (b.Position ?? 0)
  );
  if (files.length === 0) return null;
  return { version, files, versionCount: entry.VersionArray.length };
}

export interface StoredIdDocumentWithType extends StoredIdDocument {
  idLegalClientDocumentType: number;
  label: string;
  /** Die ganze Dokumentart samt aller Stände — für die Versionshistorie. */
  entry: ILegalClientDocumentTypeEntry;
}

/**
 * Der hinterlegte Ausweis eines Beteiligten, unabhängig von der Art.
 *
 * Gesucht wird über alle Ausweisarten in der Reihenfolge von
 * `ID_DOC_TYPE_ARRAY`: liegt ein Personalausweis vor, ist er gemeint, sonst der
 * Reisepass und so weiter. Gebraucht dort, wo man nur wissen will, *ob* ein
 * Ausweis vorliegt — etwa im Ident-Service-Panel, um ihn direkt ansehen zu
 * können, statt eine Einreichung anzufordern, die es vielleicht nicht braucht.
 */
export function firstStoredIdDocument(
  participant: ComparableParticipant | undefined | null
): StoredIdDocumentWithType | null {
  for (const idLegalClientDocumentType of ID_DOC_TYPE_ARRAY) {
    const stored = storedIdDocument(participant, idLegalClientDocumentType);
    const entry = idTypeEntry(participant, idLegalClientDocumentType);

    if (stored && entry) {
      return {
        ...stored,
        idLegalClientDocumentType,
        label:
          ID_DOC_TYPE_LABEL[idLegalClientDocumentType] ?? 'Ausweisdokument',
        entry,
      };
    }
  }

  return null;
}

export type ExpiryComparison =
  | 'newer'
  | 'same'
  | 'older'
  | 'unknown_submitted'
  | 'unknown_stored';

/**
 * Vergleicht das Ablaufdatum der Einreichung mit dem des hinterlegten Stands.
 *
 * `older` ist das Warnsignal: der Mandant reicht ein Dokument ein, das früher
 * abläuft als das bekannte — dann ist es entweder ein anderes Dokument oder eine
 * ältere Aufnahme.
 */
export function compareExpiry(
  submittedExpiry: string | null | undefined,
  storedValidUntil: string | null | undefined
): ExpiryComparison {
  const submitted = parseDate(submittedExpiry);
  if (!submitted) return 'unknown_submitted';
  const stored = parseDate(storedValidUntil);
  if (!stored) return 'unknown_stored';
  const diff = submitted.getTime() - stored.getTime();
  if (diff > 0) return 'newer';
  if (diff < 0) return 'older';
  return 'same';
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

export interface ExpiryComparisonText {
  text: string;
  severity: 'info' | 'success' | 'warning';
}

/** Satz für den Prüf-Dialog. */
export function expiryComparisonText(
  comparison: ExpiryComparison
): ExpiryComparisonText | null {
  switch (comparison) {
    case 'newer':
      return {
        text: 'Der eingereichte Ausweis läuft später ab als der hinterlegte — passt zu einer Verlängerung.',
        severity: 'success',
      };
    case 'same':
      return {
        text: 'Gleiches Ablaufdatum wie beim hinterlegten Ausweis — vermutlich dasselbe Dokument.',
        severity: 'info',
      };
    case 'older':
      return {
        text: 'Der eingereichte Ausweis läuft FRÜHER ab als der hinterlegte. Bitte prüfen, ob es ein anderes oder ein älteres Dokument ist.',
        severity: 'warning',
      };
    case 'unknown_stored':
      return {
        text: 'Beim hinterlegten Ausweis ist kein Ablaufdatum vermerkt — kein Vergleich möglich.',
        severity: 'info',
      };
    default:
      return null;
  }
}

/** „Version 2 von 3, hochgeladen am … von …" */
export function versionCaption(stored: StoredIdDocument): string {
  const { version, versionCount } = stored;
  const parts = [
    versionCount > 1
      ? `Version ${version.VersionNumber} von ${versionCount}`
      : `Version ${version.VersionNumber}`,
  ];
  const created = parseDateTime(version.Created_at);
  if (created) parts.push(created);
  const user = [version.UserFirstName, version.UserLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (user) parts.push(user);
  return parts.join(' · ');
}

function parseDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}
